<template>
<v-dialog
    v-model="dialog"
    max-width="920"
>
    <v-card>
        <v-card-title>Optimize Recipients</v-card-title>

        <v-card-text>
            <m-beta-alert
                code="model-r1"
                description="RedChirp AI is a beta feature and is still new &mdash; please review all changes made to your recipients carefully."
                dense
            />

            <v-alert
                v-if="optimization_history && optimization_history.has_optimization"
                border="left"
                colored-border
                color="primary"
                elevation="2"
                type="info"
                dense
            >
            "{{ $rc.enumObject('model_r1_optimization_type', optimization_history.optimization.type)?.text || optimization_history.optimization.type }}" optimization was last performed by {{ optimization_history.user_name }} at {{ $rc.pretty(optimization_history.optimization.time, 'date_and_time') }}
            <template v-if="optimization_history.has_multiple_optimizations">
                <br>
                <i>Note: This shows only the most recent optimization. There {{ optimization_history.previous_optimization_count === 1 ? 'was' : 'were' }} {{ optimization_history.previous_optimization_count }} additional optimization {{ optimization_history.previous_optimization_count === 1 ? 'run' : 'runs' }} prior.</i>
            </template>
            </v-alert>

            <v-stepper
                v-model="step"
                vertical
                flat
            >
                <v-stepper-step
                    :complete="step > 1"
                    step="1"
                >
                    <div>
                        Select Optimization Goal<template v-if="goal"> : <b>{{ $rc.enumObject('model_r1_optimization_type', goal)?.text || goal }}</b></template>
                    </div>
                </v-stepper-step>

                <v-stepper-content
                    step="1"
                >
                    <v-list
                        three-line
                    >
                        <v-list-item
                            v-on:click="onSetGoal('remove-outliers')"
                        >
                            <v-list-item-avatar>
                                <v-icon>
                                    mdi-emoticon-happy-outline
                                </v-icon>
                            </v-list-item-avatar>

                            <v-list-item-content>
                                <v-list-item-title>{{ $rc.enumObject('model_r1_optimization_type', 'remove-outliers')?.text || 'remove-outliers' }}</v-list-item-title>
                                <v-list-item-subtitle>Remove recipients if the timing isn't ideal (e.g., they've purchased too recently, just received another promotion, etc).</v-list-item-subtitle>
                            </v-list-item-content>
                        </v-list-item>

                        <v-list-item
                            v-on:click="onSetGoal('maximize-return')"
                        >
                            <v-list-item-avatar>
                                <v-icon>
                                    mdi-bullseye-arrow
                                </v-icon>
                            </v-list-item-avatar>

                            <v-list-item-content>
                                <v-list-item-title>{{ $rc.enumObject('model_r1_optimization_type', 'maximize-return')?.text || 'maximize-return' }}</v-list-item-title>
                                <v-list-item-subtitle>Optimize your list down to a specific number of recipients (you'll select the exact number in step #2).</v-list-item-subtitle>
                            </v-list-item-content>
                        </v-list-item>

                    </v-list>
                </v-stepper-content>

                    <v-stepper-step
                        :complete="step > 2"
                        step="2"
                    >
                        Customize Goal
                    </v-stepper-step>

                    <v-stepper-content
                        step="2"
                    >
                        <div
                            v-if="goal == 'maximize-return'"
                        >
                            <v-slider
                                :min="1"
                                :max="recipient_count"
                                :step="1"
                                label="Number of recipients"
                                v-model="target_recipient_count"
                                thumb-label="always"
                                style="margin-top: 35px"
                            >
                                <template v-slot:append>
                                    <v-text-field
                                        v-model="target_recipient_count"
                                        class="mt-0 pt-0"
                                        hide-details
                                        single-line
                                        type="number"
                                        style="width: 60px"
                                    />
                                </template>
                            </v-slider>

                            <div
                                style="margin-bottom: 15px"
                                v-if="!show_help_content"
                            >
                                <a
                                    v-on:click="show_help_content = true"
                                >
                                    How does this optimization work?
                                </a>
                            </div>

                            <div
                                v-if="show_help_content"
                            >
                                <p><b>How does the maximize return optimization work?</b></p>
                                <p>
                                    The "Choose a Specific Number of Recipients" optimization helps you send to a strategically balanced list that maximizes return while also gathering valuable data on less-known recipients. RedChirp AI will aim to create a list with the following composition:
                                </p>
                                <p>
                                    <ul>
                                        <li><strong>75% High-confidence recipients:</strong> Those with a high sentiment score and a strong likelihood of responding positively.</li>
                                        <li><strong>25% Test group:</strong> Recipients RedChirp knows less about or haven't received a message recently. This helps you improve future targeting and keep newer customers engaged.</li>
                                    </ul>
                                </p>
                            </div>
                        </div>

                         <div
                            v-if="goal == 'remove-outliers'"
                        >
                            When deciding which recipients to remove consider:

                            <v-checkbox
                                label="Date of last purchase"
                                style="margin-bottom: -25px"
                                v-model="target_remove_recent_purchasers"
                            />

                            <v-checkbox
                                label="Date last promotional message sent"
                                style="margin-bottom: -25px"
                                v-model="target_remove_recent_recipients"
                            />

                            <!--
                                In the near future:
                                    Expired or declined payment method on file
                                    Upcoming reservation (i.e. don't message if they have a reservation in the next 30 days)
                            -->

                            <v-checkbox
                                label="Opt-out of promotional messages"
                                v-model="target_remove_opt_outs"
                                disabled
                            />

                        </div>

                         <div
                            style="text-align: left; margin-bottom: 15px"
                        >
                            <v-btn
                                color="primary"
                                style="margin-right: 15px"
                                v-on:click="onClickPreviewResults"
                            >
                                Preview Results
                            </v-btn>

                            <v-btn
                                v-on:click="onClickChangeGoal"
                            >
                                Change Goal
                            </v-btn>


                        </div>

                    </v-stepper-content>


                        <v-stepper-step
                            :complete="step > 3"
                            step="3"
                        >
                            Preview Results
                        </v-stepper-step>

                        <v-stepper-content
                            step="3"
                        >
                            <p>If applied, this optimization will result in the removal of:</p>
                            <p>
                                <ul>
                                    <li
                                        v-for="(item, index) in removal_bullets"
                                        :key="'rr_'+index"
                                    >
                                        {{item}}
                                    </li>
                                </ul>
                            </p>
                            <v-icon>
                                mdi-microsoft-excel
                            </v-icon>
                            <a style="text-decoration: underline" v-on:click="onClickExcelExportPreview">Optimization Preview</a>

                        </v-stepper-content>
              </v-stepper>


        </v-card-text>

        <v-card-actions>
            <v-btn
                text
                v-on:click="onClickSentimentForecast"
            >
                Advanced: AI/ML Sentiment Forecast
            </v-btn>

            <v-spacer></v-spacer>

            <v-btn
                text
                @click="onCancel"
            >
            Cancel
            </v-btn>

            <v-btn
                color="primary darken-1"
                text
                @click="onOk"
                :disabled="step != 3 || read_only"
            >
                {{read_only ? 'Apply Optimization (disabled, read only)' : 'Apply Optimization'}}
            </v-btn>

        </v-card-actions>

    </v-card>
</v-dialog>
</template>

<script>

export default {

    data() {
        return {
            dialog: false,
            goal: null,
            step: 1,

            target_recipient_count: null,

            target_remove_recent_purchasers: true,
            target_remove_recent_recipients: true,
            target_remove_opt_outs: true,

            show_help_content: false,
            removal_bullets: [],
            recipient_count: null,
            read_only: false,
            optimization_history: null,
        }
    },

    methods: {

        fetchOptimizationHistory() {
            this.$rc.rest({
                'spinner': false,
                'endpoint': 'GET: /api/v1/model/r1/optimization-history/:bulk_message_id',

                'params' : {
                    'bulk_message_id': this.$route.params.bulk_message_id,
                },

                'on_success': (data) => {
                    this.optimization_history = data;
                },

                'on_error': () => {
                    this.optimization_history = null;
                },
            });
        },

        showDialog(recipient_count, read_only)
        {
            this.step = 1;
            this.goal = null;
            this.recipient_count = recipient_count;
            this.target_recipient_count = Math.floor(this.recipient_count * 0.75);

            this.target_remove_recent_purchasers = true;
            this.target_remove_recent_recipients = true;
            this.target_remove_opt_outs = true;
            this.show_help_content  = false;
            this.removal_bullets = [];
            this.read_only = read_only;
            this.optimization_history = null;

            this.fetchOptimizationHistory();
            this.dialog = true;
        },

        onClickChangeGoal()
        {
            this.step = 1;
            this.goal = null;
            this.show_help_content  = false;
        },

        onClickPreviewResults()
        {
            this.$rc.startProcess({
                'type': 'inference-model-r1',

                'params': {
                    'bulk_message_id': this.$route.params.bulk_message_id
                },

                'title': 'Optimize Recipients: Updating Sentiment Forecast',
                'finished_button_text': 'Ok',
                'failed_button_text': 'Ok',

                status: (pte) => {
                    switch (pte.state)
                    {
                        case 'failed': return 'Unable to update sentiment forecast';
                        case 'finished': return 'Sentiment forecast complete.';
                    }

                    if ( pte.progress_counter <= 2 )
                    {
                        return 'Getting ready to update sentiment forecast: this may take several minutes';
                    }

                    return 'Sentiment forecast complete: ' + pte.progress_counter;
                },

                on_finished: () => {
                    this.onInferenceComplete();
                },

                on_failed: () => {
                    this.$rc.alert('Unable to update sentiment forecast', 'There was an unexpected error while attempting to update the sentiment forecast.  Please try a again.');
                },

            });
        },

        onInferenceComplete()
        {
            if ( this.goal == 'maximize-return' )
            {
                this.target_remove_recent_purchasers = false;
                this.target_remove_recent_recipients = false;
            }
            else
            {
                this.target_recipient_count = null;
            }

            this.$rc.rest({
                'spinner': false,
                'endpoint': 'PUT: /api/v1/model/r1/optimize/summary',

                'params' : this.getOptimizeParams(),

                'on_success': (data) => {
                        this.removal_bullets = data.removal_bullets;
                        this.step = 3;
                    },

                'on_error': 'Unable to get optimization preview',
            });
        },

        getOptimizeParams()
        {
            return {
                'bulk_message_id': this.$route.params.bulk_message_id,
                'organization_id': this.$route.params.organization_id,
                'goal': this.goal,
                'target_recipient_count': this.target_recipient_count,
                'target_remove_recent_purchasers': this.target_remove_recent_purchasers,
                'target_remove_recent_recipients': this.target_remove_recent_recipients,
                'target_remove_opt_outs': true
            };
        },

        onOk()
        {
            this.$rc.rest({
                'spinner': false,
                'endpoint': 'PUT: /api/v1/model/r1/optimize/apply',

                'params' : this.getOptimizeParams(),

                'on_success': (data) => {
                        this.dialog = false;
                        this.$emit('ok');
                        this.$rc.snackbar('Removed '+data.removed_recipient_count+' recipient(s)');
                        // After applying optimization, the history will be updated
                        this.fetchOptimizationHistory();
                    },

                'on_error': 'Unable to apply optimize recipients',
            });


        },

        onCancel()
        {
            this.dialog = false;
        },

        onSetGoal(goal)
        {
            this.goal = goal;
            this.step = 2;
        },

        onClickSentimentForecast()
        {
            this.$router.push({
                'name': 'bulk-message-sentiment-forecast',
                'params' : {
                    'organization_id': this.$route.params.organization_id,
                    'bulk_message_id': this.$route.params.bulk_message_id,
                }
            });
        },

        onClickExcelExportPreview()
        {
            this.$rc.excelExport(
                    'EXPORT: /api/v1/model/r1/optimize/export',
                    this.getOptimizeParams()
                );
        },

    },
}
</script>

<style lang="scss" scoped>

</style>